import { InfoIcon } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import VerificationInput from "react-verification-input";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";

interface CheckoutOtpFormProps {
  onSubmit: (code: string) => void;
  onResend: () => void;
  maskedName?: { firstName?: string; lastName?: string } | null;
  isLoading?: boolean;
  error?: string | null;
  resendCount: number;
  cooldownEnd: number | null;
  purpose?: "correlation" | "registration";
  hideBanner?: boolean;
  bannerMessage?: string;
  // Bump from the parent to wipe the OTP input — used after a wrong-code rejection.
  // We don't tie this to `error` because resend failures also set `error` and we
  // shouldn't clear what the user has already typed when only the resend failed.
  resetSignal?: number;
  // Backend reported the underlying TFA session is gone — locks the input, replaces
  // the submit CTA with a retry that re-opens phone verification, and hides resend.
  isSessionExpired?: boolean;
  onRetry?: () => void;
  // Override the submit button label. Default keeps the generic "Potwierdź kod" so
  // HasAccountOTP (correlation) is untouched; NewUserOtpOverlay passes the
  // payment/free-order variant since confirming the code there commits the order.
  submitLabelKey?: string;
}

export const CheckoutOtpForm = ({
  onSubmit,
  onResend,
  maskedName,
  isLoading,
  error,
  resendCount,
  cooldownEnd,
  purpose = "correlation",
  hideBanner = false,
  bannerMessage,
  resetSignal,
  isSessionExpired = false,
  onRetry,
  submitLabelKey = "VOUCHERS_DISTRIBUTION.PHONE_AUTH.CONFIRM_CODE",
}: CheckoutOtpFormProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetSignal is the trigger; we deliberately don't read it inside the effect
  useEffect(() => {
    setCode("");
  }, [resetSignal]);

  useEffect(() => {
    if (!cooldownEnd) {
      setTimeLeft(0);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
      setTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [cooldownEnd]);

  const handleSubmit = () => {
    if (code.length === 6) {
      onSubmit(code);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {!hideBanner && (
        <div className="mt-4 flex items-start gap-3 rounded-xl bg-blue-50 p-4 text-blue-800 text-sm leading-relaxed">
          <InfoIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-blue-600" />
          <p>{bannerMessage ?? t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.IDENTITY_VERIFICATION_NOTICE")}</p>
        </div>
      )}

      {/* biome-ignore lint/a11y/noStaticElementInteractions: intercept enter key for OTP */}
      <div
        className="flex flex-col items-center gap-4"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            if (isSessionExpired) {
              onRetry?.();
            } else {
              handleSubmit();
            }
          }
        }}
      >
        <div className="flex w-full flex-col gap-2">
          <span className="text-center font-medium text-gray-700 text-sm">
            {t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.ENTER_SIX_DIGIT_CODE")}
          </span>
          <div className={cn("flex justify-center py-5", isSessionExpired && "pointer-events-none opacity-50")}>
            <VerificationInput
              value={code}
              onChange={setCode}
              validChars="0-9"
              placeholder=""
              inputProps={{ disabled: isSessionExpired }}
              classNames={{
                container: "w-full max-w-[320px] gap-2",
                character:
                  "h-12 w-12 rounded-xl border border-gray-200 bg-white text-xl font-medium text-gray-900 flex items-center justify-center shadow-sm",
                characterInactive: "bg-gray-50",
                characterSelected: "border-blue-600 ring-2 ring-blue-100",
              }}
            />
          </div>
          {error && <p className="mt-1 text-center font-medium text-destructive text-sm">{error}</p>}
        </div>

        {isSessionExpired ? (
          <Button
            type="button"
            onClick={onRetry}
            disabled={isLoading}
            variant={isLoading ? "tertiary" : "secondary"}
            className={cn(
              "w-full max-w-[460px]",
              isLoading && "disabled:opacity-60",
              !isLoading && "border-none bg-dodger-blue-700 text-white hover:bg-dodger-blue-700",
            )}
          >
            {t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.TRY_AGAIN")}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={code.length !== 6 || isLoading}
            variant={code.length !== 6 || isLoading ? "tertiary" : "secondary"}
            className={cn(
              "w-full max-w-[460px]",
              (code.length !== 6 || isLoading) && "disabled:opacity-60",
              !isLoading && code.length === 6 && "border-none bg-dodger-blue-700 text-white hover:bg-dodger-blue-700",
            )}
          >
            {t(submitLabelKey)}
          </Button>
        )}

        {!isSessionExpired && resendCount === 0 && (
          <div className="text-sm">
            <button
              type="button"
              onClick={onResend}
              disabled={timeLeft > 0 || isLoading}
              className="text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:hover:text-gray-400"
            >
              {timeLeft > 0
                ? t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.RESEND_CODE_COUNTDOWN", { timeLeft })
                : t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.RESEND_CODE")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
